import React, { useCallback, useState } from 'react';
import { useContextMenu } from 'react-contexify';

export type ContextExpandSide = 'left' | 'right';

export type HandleShowContextMenu<T> = (
  id: string,
  event: React.MouseEvent<Element, MouseEvent>,
  props: T,
  openBelowEventTargetElement?: ContextExpandSide
) => void;

type HandleHideAll = () => void;

type ContextMenuIsOpen = boolean;

const contextMenuWidth = 200;

interface ContextMenuProviderProps<T> {
  clickedSetState?: React.Dispatch<React.SetStateAction<T | null>>;
}

export const useShowContextMenu = <T,>({ clickedSetState: clickedPropsDispatch }: ContextMenuProviderProps<T> = {}): [
  HandleShowContextMenu<T>,
  HandleHideAll,
  ContextMenuIsOpen,
] => {
  const { show, hideAll } = useContextMenu();
  const [contextMenuIsOpen, setContextMenuIsOpen] = useState<boolean>(false);

  const handleHideAll = useCallback(() => {
    hideAll();
    setContextMenuIsOpen(false);
  }, [hideAll, setContextMenuIsOpen]);

  const handleShowContextMenu = useCallback(
    (
      id: string,
      event: React.MouseEvent<Element, MouseEvent>,
      props: T,
      openBelowEventTargetElement?: ContextExpandSide
    ) => {
      event.preventDefault();
      clickedPropsDispatch?.(props);
      let position: { x: number; y: number } | undefined;
      if (openBelowEventTargetElement === 'left') {
        const rect = event.currentTarget.getBoundingClientRect();
        position = {
          x: rect.x - contextMenuWidth + rect.width,
          y: rect.y + rect.height,
        };
      }
      if (openBelowEventTargetElement === 'right') {
        const rect = event.currentTarget.getBoundingClientRect();
        position = {
          x: rect.x,
          y: rect.y + rect.height,
        };
      }
      show({
        event,
        id,
        props,
        position,
      });
      setContextMenuIsOpen(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clickedPropsDispatch]
  );

  return [handleShowContextMenu, handleHideAll, contextMenuIsOpen];
};
